import { currencies } from "@megaron/catalog-contracts";
import { HttpAction } from "@megaron/http-service";
import { Serializers, SerializerValue } from "@megaron/serializers";
import { uuidSerializer } from "@megaron/uuid";

import { customerAddressSerializer } from "./customers";

export const myOrderSortFields = ["placedAt"] as const;
export type MyOrderSortField = (typeof myOrderSortFields)[number];

const orderDraftLineDtoSerializer = Serializers.object({
  uuid: uuidSerializer,
  item: Serializers.object({ itemId: Serializers.string, name: Serializers.string }),
  promoPrice: Serializers.decimal.nullable(),
  quantity: Serializers.integer,
  lineNetTotal: Serializers.decimal,
  finalPrice: Serializers.decimal,
  basePrice: Serializers.decimal,
  lineDiscountRate: Serializers.decimal,
});

export type OrderDraftLineDto = SerializerValue<typeof orderDraftLineDtoSerializer>;

export const orderDraftChargeDtoSerializer = Serializers.object({
  itemNumber: Serializers.string,
  quantity: Serializers.decimal,
  price: Serializers.decimal,
});

export type OrderDraftChargeDto = SerializerValue<typeof orderDraftChargeDtoSerializer>;

export const orderDraftDtoSerializer = Serializers.object({
  uuid: uuidSerializer,
  payerDiscountRate: Serializers.decimal,

  payerDepartmentUuid: uuidSerializer,
  payerDepartmentName: Serializers.string,
  payerDepartmentAlert: Serializers.string.nullable(),

  billToUuid: uuidSerializer,
  billToName: Serializers.string,
  billToAlert: Serializers.string.nullable(),

  sellToUuid: uuidSerializer,
  sellToName: Serializers.string,
  sellToAlert: Serializers.string.nullable(),
  sellToRegion: Serializers.string.nullable(),
  payerDepartmentAddress: customerAddressSerializer,

  isFinalized: Serializers.boolean,
  finalizedAt: Serializers.datetime.nullable(),
  priceList: Serializers.string,
  lines: Serializers.array(orderDraftLineDtoSerializer),
  charges: Serializers.array(orderDraftChargeDtoSerializer),
  ownerId: Serializers.string,
  isDiscarded: Serializers.boolean,
  startedAt: Serializers.datetime,
  discardedAt: Serializers.datetime.nullable(),
  netTotal: Serializers.decimal,
  currency: Serializers.stringOneOf(...currencies),
});

export type OrderDraftDto = SerializerValue<typeof orderDraftDtoSerializer>;

export const myDraftSimpleCustomerSerializer = Serializers.object({
  uuid: uuidSerializer,
  name: Serializers.string.optional(),
});

export const createDraftCopyInputSerializer = Serializers.object({
  uuid: uuidSerializer,
  templateUuid: uuidSerializer,
});

export const myDraftSimpleSerializer = Serializers.object({
  uuid: uuidSerializer,
  buyer: myDraftSimpleCustomerSerializer,
  payer: myDraftSimpleCustomerSerializer,
  placedAt: Serializers.datetime.nullable(),
  draftStartedAt: Serializers.datetime,
  isPlaced: Serializers.boolean,
});

export const addDraftLineInputSerializer = Serializers.object({
  lineUuid: uuidSerializer,
  item: Serializers.object({ itemId: Serializers.string }),
  quantity: Serializers.integer,
  promoPrice: Serializers.decimal.nullable(),
  lineDiscountRate: Serializers.decimal,
});

export type AddDraftLineInput = SerializerValue<typeof addDraftLineInputSerializer>;

export const updateDraftLineInputSerializer = Serializers.object({
  quantity: Serializers.integer,
  promoPrice: Serializers.decimal.nullable(),
  lineDiscountRate: Serializers.decimal,
});

export type UpdateDraftLineInput = SerializerValue<typeof updateDraftLineInputSerializer>;

export const startDraftInputSerializer = Serializers.object({
  uuid: uuidSerializer,
  sellToUuid: uuidSerializer,
  billToUuid: uuidSerializer,
  payerDepartmentUuid: uuidSerializer,
});

export type DraftInput = SerializerValue<typeof startDraftInputSerializer>;

export const setDraftChargesInputSerializer = Serializers.object({
  charges: Serializers.array(orderDraftChargeDtoSerializer),
});

export const placeOrderInputSerializer = Serializers.object({
  deliveryAddress: customerAddressSerializer,
  deliveryDeadline: Serializers.dateOnly,
  externalOrderNumber: Serializers.string,
  comment: Serializers.string,
});

export const editCustomersInputSerializer = Serializers.object({
  sellToUuid: uuidSerializer,
  billToUuid: uuidSerializer,
  payerDepartmentUuid: uuidSerializer,
});

export type EditCustomersInput = SerializerValue<typeof editCustomersInputSerializer>;

const myOrderDraftDtoSerializer = Serializers.object({
  uuid: uuidSerializer,
  placedAt: Serializers.datetime.nullable(),
  draftStartedAt: Serializers.datetime,
  isPlaced: Serializers.boolean,
  sellToName: Serializers.string,
  sellToUuid: uuidSerializer,
  payerDepartmentAddress: customerAddressSerializer,
  billToName: Serializers.string,
  billToUuid: uuidSerializer,
});

export type MyOrderDraftDto = SerializerValue<typeof myOrderDraftDtoSerializer>;

export const orderDraftActions = {
  myDraftListQuery: HttpAction({
    path: "/sales/orderDrafts",
    method: "get",
    requiresAuth: true as const,
    valueSerializer: Serializers.paginatedList(myOrderDraftDtoSerializer),
    errorSerializer: Serializers.stringOneOf("DraftNotFound", "PriceListNotFound", "DraftDiscarded", "DraftFinalized"),
  }),

  myDraftQuery: HttpAction({
    path: "/sales/orderDrafts/:uuid",
    method: "get",
    requiresAuth: true as const,
    paramsSerializer: Serializers.object({ uuid: uuidSerializer }),
    valueSerializer: orderDraftDtoSerializer,
    errorSerializer: Serializers.stringOneOf("DraftNotFound", "PriceListNotFound", "DraftDiscarded", "DraftFinalized"),
  }),

  startDraft: HttpAction({
    path: "/sales/orderDrafts",
    method: "post",
    requiresAuth: true as const,
    bodySerializer: startDraftInputSerializer,
    errorSerializer: Serializers.stringOneOf("PriceListNotFound"),
  }),

  placeOrder: HttpAction({
    path: "/sales/orderDrafts/:uuid/place",
    method: "post",
    requiresAuth: true as const,
    paramsSerializer: Serializers.object({ uuid: uuidSerializer }),
    bodySerializer: placeOrderInputSerializer,
    errorSerializer: Serializers.stringOneOf(
      "PriceListNotFound",
      "OrderNotFound",
      "DraftNotOpen",
      "ItemNotFound",
      "UserNotOrderOwner",
    ),
  }),
  discardDraft: HttpAction({
    path: "/sales/orderDrafts/:uuid",
    method: "delete",
    requiresAuth: true as const,
    paramsSerializer: Serializers.object({ uuid: uuidSerializer }),
    errorSerializer: Serializers.stringOneOf("OrderNotFound", "DraftNotOpen", "UserNotOrderOwner"),
  }),
  editDraftCustomers: HttpAction({
    path: "/sales/orderDrafts/:uuid/customers",
    method: "put",
    requiresAuth: true as const,
    paramsSerializer: Serializers.object({ uuid: uuidSerializer }),
    bodySerializer: editCustomersInputSerializer,
    errorSerializer: Serializers.stringOneOf("OrderNotFound", "DraftNotOpen", "UserNotOrderOwner", "PriceListNotFound"),
  }),
  addDraftLine: HttpAction({
    path: "/sales/orderDrafts/:uuid/lines",
    method: "post",
    requiresAuth: true as const,
    paramsSerializer: Serializers.object({ uuid: uuidSerializer }),
    bodySerializer: addDraftLineInputSerializer,
    errorSerializer: Serializers.stringOneOf(
      "OrderNotFound",
      "DraftNotOpen",
      "UserNotOrderOwner",
      "LineAlreadyExists",
      "InvalidLineQuantity",
      "InvalidLineDiscountRate",
    ),
  }),
  updateDraftLine: HttpAction({
    path: "/sales/orderDrafts/:uuid/lines/:lineUuid",
    method: "put",
    requiresAuth: true as const,
    paramsSerializer: Serializers.object({
      uuid: uuidSerializer,
      lineUuid: uuidSerializer,
    }),
    bodySerializer: updateDraftLineInputSerializer,
    errorSerializer: Serializers.stringOneOf(
      "OrderNotFound",
      "DraftNotOpen",
      "UserNotOrderOwner",
      "LineDoesNotExist",
      "InvalidLineQuantity",
      "InvalidLineDiscountRate",
    ),
  }),
  removeDraftLine: HttpAction({
    path: "/sales/orderDrafts/:uuid/lines/:lineUuid",
    method: "delete",
    requiresAuth: true as const,
    paramsSerializer: Serializers.object({
      uuid: uuidSerializer,
      lineUuid: uuidSerializer,
    }),
    errorSerializer: Serializers.stringOneOf("OrderNotFound", "DraftNotOpen", "UserNotOrderOwner", "LineDoesNotExist"),
  }),
  setDraftCharges: HttpAction({
    path: "/sales/orderDrafts/:uuid/charges",
    method: "put",
    requiresAuth: true as const,
    paramsSerializer: Serializers.object({ uuid: uuidSerializer }),
    bodySerializer: setDraftChargesInputSerializer,
    errorSerializer: Serializers.stringOneOf("OrderNotFound", "DraftNotOpen", "UserNotOrderOwner"),
  }),
  createDraftCopy: HttpAction({
    path: "/sales/orderDrafts/createCopy",
    method: "post",
    requiresAuth: true as const,
    bodySerializer: createDraftCopyInputSerializer,
    errorSerializer: Serializers.stringOneOf("DraftNotFound"),
  }),
};
